/**
 * The Erectus figure: a person seated at a desk, side view, facing right.
 *
 * Single source for the app icon, the native launch images and the splash
 * animation (scripts/render-icons.mjs imports this file). Coordinates are in a
 * 512×512 frame; the upright pose's bounding box is centred in it, and every
 * pose shares the frame, so animating between poses never shifts the figure.
 *
 * The slouched pose is forward head plus a rounded upper back: the trunk leans
 * forward, the spine bows backward, and the head sits ahead of the shoulders
 * instead of stacked above them — what the app measures, drawn.
 */
export interface Point {
  x: number;
  y: number;
}

export interface Pose {
  hip: Point;
  knee: Point;
  foot: Point;
  shoulder: Point;
  /** How far the spine bows backward from a straight line, in frame units. */
  spineBow: number;
  /** Head direction from the shoulder, degrees from vertical; positive = forward. */
  headAngle: number;
  /** Shoulder to head centre. Longer when the head juts forward. */
  neck: number;
  /** Hand resting on the desk. */
  hand: Point;
}

export const STROKE = 34;
export const HEAD_RADIUS = 46;
/** Where the arm leaves the spine, as a fraction from hip (0) to shoulder (1). */
const ARM_ROOT = 100 / 140;

export const UPRIGHT: Pose = {
  hip: { x: 231, y: 346 },
  knee: { x: 319, y: 346 },
  foot: { x: 327, y: 414 },
  shoulder: { x: 231, y: 206 },
  spineBow: 0,
  headAngle: 0,
  neck: 62,
  hand: { x: 311, y: 282 },
};

export const SLOUCHED: Pose = {
  hip: { x: 231, y: 346 },
  knee: { x: 319, y: 346 },
  foot: { x: 327, y: 414 },
  shoulder: { x: 270, y: 226 },
  spineBow: 30,
  headAngle: 56,
  neck: 86,
  hand: { x: 322, y: 300 },
};

/** Figure height in frame units (upright), for sizing against a screen. */
export const FIGURE_HEIGHT =
  UPRIGHT.foot.y + STROKE / 2 - (UPRIGHT.shoulder.y - UPRIGHT.neck - HEAD_RADIUS - STROKE / 2);

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const lerpPoint = (a: Point, b: Point, t: number): Point => ({ x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t) });

/** The pose `t` of the way from `from` to `to`. */
export function blend(from: Pose, to: Pose, t: number): Pose {
  return {
    hip: lerpPoint(from.hip, to.hip, t),
    knee: lerpPoint(from.knee, to.knee, t),
    foot: lerpPoint(from.foot, to.foot, t),
    shoulder: lerpPoint(from.shoulder, to.shoulder, t),
    spineBow: lerp(from.spineBow, to.spineBow, t),
    headAngle: lerp(from.headAngle, to.headAngle, t),
    neck: lerp(from.neck, to.neck, t),
    hand: lerpPoint(from.hand, to.hand, t),
  };
}

export interface FigureShape {
  /** All limbs and the spine as one stroked path. */
  d: string;
  head: { cx: number; cy: number; r: number };
}

const r1 = (n: number): number => Math.round(n * 10) / 10;

export function shape(pose: Pose): FigureShape {
  const { hip, knee, foot, shoulder, hand } = pose;
  // Spine: a quadratic curve from hip to shoulder, bowed backward (the
  // person faces +x, so "back" is the left-hand normal of hip→shoulder).
  const dx = shoulder.x - hip.x;
  const dy = shoulder.y - hip.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = dy / len;
  const ny = -dx / len;
  const control = {
    x: (hip.x + shoulder.x) / 2 + nx * pose.spineBow * 2,
    y: (hip.y + shoulder.y) / 2 + ny * pose.spineBow * 2,
  };
  const at = (u: number): Point => ({
    x: (1 - u) ** 2 * hip.x + 2 * (1 - u) * u * control.x + u ** 2 * shoulder.x,
    y: (1 - u) ** 2 * hip.y + 2 * (1 - u) * u * control.y + u ** 2 * shoulder.y,
  });
  const armRoot = at(ARM_ROOT);
  const a = (pose.headAngle * Math.PI) / 180;
  const head = { cx: shoulder.x + pose.neck * Math.sin(a), cy: shoulder.y - pose.neck * Math.cos(a) };

  const d = [
    `M${r1(hip.x)} ${r1(hip.y)}Q${r1(control.x)} ${r1(control.y)} ${r1(shoulder.x)} ${r1(shoulder.y)}`,
    `M${r1(hip.x)} ${r1(hip.y)}L${r1(knee.x)} ${r1(knee.y)}L${r1(foot.x)} ${r1(foot.y)}`,
    `M${r1(armRoot.x)} ${r1(armRoot.y)}L${r1(hand.x)} ${r1(hand.y)}`,
  ].join('');
  return { d, head: { cx: r1(head.cx), cy: r1(head.cy), r: HEAD_RADIUS } };
}

/** SVG markup for a pose, in the 512 frame, scaled by `scale` about (cx, cy). */
export function figureMarkup(
  pose: Pose,
  { color, scale = 1, cx = 256, cy = 256 }: { color: string; scale?: number; cx?: number; cy?: number },
): string {
  const { d, head } = shape(pose);
  const t = `translate(${cx} ${cy}) scale(${scale}) translate(-256 -256)`;
  return `<g transform="${t}" fill="none" stroke="${color}" stroke-width="${STROKE}" stroke-linecap="round" stroke-linejoin="round"><path d="${d}"/><circle cx="${head.cx}" cy="${head.cy}" r="${head.r}"/></g>`;
}

/**
 * Colours, taken from the app itself so the icon, the launch screen and the
 * first screen read as one thing: the app's light surface as the ground, slate
 * while slumped, and the app's own "good posture" green once upright (the
 * colour a good score is shown in). 2026-10-06: replaced an ink ground with a
 * neon green the app never used, which made launch flip dark → light.
 */
export const BRAND = {
  ground: '#f8fafc',
  slouched: '#94a3b8',
  upright: '#15803d',
} as const;

/**
 * Splash geometry: the figure is this fraction of the screen's height. The
 * native launch images use the same number so the hand-off to the animated
 * splash does not move the figure.
 */
export const SPLASH_FIGURE_FRACTION = 0.22;
